import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import {
  Build as BuildIcon,
  Description as DescriptionIcon,
  List as ListIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/context";
import lotDetailsService from "../../services/lotDetailsService";

// Opciones de sección para el PDF de cotización (espejo del backend)
const QUOTE_SECTION_OPTIONS = [
  { value: "", label: "Sin asignar" },
  { value: "characteristics", label: "Características" },
  { value: "documentation", label: "Documentación" },
];

const sectionIcon = (value, fontSize = "small") => {
  if (value === "characteristics")
    return <BuildIcon fontSize={fontSize} color="primary" />;
  if (value === "documentation")
    return <DescriptionIcon fontSize={fontSize} color="secondary" />;
  return <ListIcon fontSize={fontSize} color="disabled" />;
};

const avatarBg = (value) => {
  if (value === "characteristics") return "primary.main";
  if (value === "documentation") return "secondary.main";
  return "grey.400";
};

/**
 * Sección dentro del modal de Detalle de Precio para indicar qué líneas de
 * detalle del lote aparecen como "Características" o "Documentación" en el PDF
 * de cotización de venta de usados.
 *
 * Alcance (Opción A): solo asigna la sección de líneas existentes. No crea,
 * edita ni elimina líneas de detalle.
 *
 * @param {Object} props
 * @param {boolean} props.open - Si el modal contenedor está abierto (dispara la carga)
 * @param {Object} props.product - Producto del catálogo (product.id === lotId)
 * @param {(dirty:boolean) => void} [props.onDirtyChange] - Notifica si hay cambios sin guardar
 * @param {React.Ref} ref - Expone { save, hasChanges } para el guardado global del modal
 */
const LotDetailsSections = forwardRef(
  ({ open, product, onDirtyChange }, ref) => {
    const { token } = useAuth();
    const lotId = product?.id;

    const [lines, setLines] = useState([]);
    const [original, setOriginal] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Cargar líneas al abrir
    useEffect(() => {
      if (!open || !lotId) return;

      let active = true;
      setError(null);
      setLines([]);
      setOriginal({});
      setLoading(true);

      lotDetailsService
        .getLotDetails(lotId, token)
        .then((data) => {
          if (!active) return;
          const normalized = (data || []).map((d) => ({
            ...d,
            quoteSection: d.quoteSection || "",
          }));
          setLines(normalized);
          // Snapshot del estado original para calcular el diff al guardar
          setOriginal(
            normalized.reduce((acc, d) => {
              acc[d.id] = d.quoteSection || "";
              return acc;
            }, {}),
          );
        })
        .catch((err) => {
          if (!active) return;
          setError(err.message || "Error al cargar el detalle del lote");
        })
        .finally(() => {
          if (active) setLoading(false);
        });

      return () => {
        active = false;
      };
    }, [open, lotId, token]);

    const handleSectionChange = useCallback((id, value) => {
      setLines((prev) =>
        prev.map((line) =>
          line.id === id ? { ...line, quoteSection: value } : line,
        ),
      );
    }, []);

    // Solo las líneas cuya sección cambió respecto al snapshot original
    const changedSections = useMemo(
      () =>
        lines
          .filter(
            (line) => (line.quoteSection || "") !== (original[line.id] || ""),
          )
          .map((line) => ({
            id: line.id,
            quoteSection: line.quoteSection || null,
          })),
      [lines, original],
    );

    const hasChanges = changedSections.length > 0;

    // Avisar al modal contenedor si hay cambios sin guardar
    useEffect(() => {
      if (onDirtyChange) onDirtyChange(hasChanges);
    }, [hasChanges, onDirtyChange]);

    const counts = useMemo(
      () => ({
        characteristics: lines.filter(
          (l) => l.quoteSection === "characteristics",
        ).length,
        documentation: lines.filter((l) => l.quoteSection === "documentation")
          .length,
      }),
      [lines],
    );

    // Persiste los cambios. Lo invoca el modal a través del ref.
    // Devuelve { saved:boolean, error?:string } sin mostrar snackbars
    // (de eso se encarga el guardado global del modal).
    const save = useCallback(async () => {
      if (!hasChanges) return { saved: false };
      try {
        const updated = await lotDetailsService.updateLotDetailSections(
          lotId,
          changedSections,
          token,
        );

        const normalized = (updated || []).map((d) => ({
          ...d,
          quoteSection: d.quoteSection || "",
        }));
        setLines(normalized);
        setOriginal(
          normalized.reduce((acc, d) => {
            acc[d.id] = d.quoteSection || "";
            return acc;
          }, {}),
        );
        return { saved: true };
      } catch (err) {
        const message =
          err.message || "Error al guardar las secciones de cotización";
        setError(message);
        return { saved: false, error: message };
      }
    }, [hasChanges, changedSections, lotId, token]);

    useImperativeHandle(ref, () => ({ save, hasChanges }), [save, hasChanges]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Características y Documentación del PDF
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Indica en qué sección del PDF de cotización aparece cada dato del lote.
          Los datos sin asignar no se muestran en la cotización.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          <Chip
            icon={<BuildIcon sx={{ fontSize: 16 }} />}
            color="primary"
            variant="outlined"
            size="small"
            label={`Características: ${counts.characteristics}`}
          />
          <Chip
            icon={<DescriptionIcon sx={{ fontSize: 16 }} />}
            color="secondary"
            variant="outlined"
            size="small"
            label={`Documentación: ${counts.documentation}`}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {lines.length === 0 ? (
        <Alert severity="info">
          Este lote no tiene líneas de detalle (Marca, Modelo, Año, etc.)
          configuradas en el administrador de lotes.
        </Alert>
      ) : (
        <List>
          {lines.map((line) => (
            <ListItem key={line.id} divider sx={{ py: 1.5 }}>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: avatarBg(line.quoteSection) }}>
                  {sectionIcon(line.quoteSection, "medium")}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                disableTypography
                primary={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: { xs: "wrap", md: "nowrap" },
                    }}
                  >
                    <Box sx={{ minWidth: 120, flex: "0 0 auto" }}>
                      <Typography variant="body1" fontWeight="medium">
                        {line.label}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 100 }}>
                      <Typography variant="body2" color="text.secondary">
                        {line.value}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 200, flex: "0 0 auto" }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id={`section-label-${line.id}`}>
                          Sección
                        </InputLabel>
                        <Select
                          labelId={`section-label-${line.id}`}
                          value={line.quoteSection || ""}
                          label="Sección"
                          onChange={(e) =>
                            handleSectionChange(line.id, e.target.value)
                          }
                        >
                          {QUOTE_SECTION_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {sectionIcon(opt.value)}
                                {opt.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
  },
);

export default LotDetailsSections;
